import { useEffect } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import {
  authenticateWithUaePass,
  linkUaePass,
} from '../../lib/auth-api';
import {
  beginUaePassCallbackProcessing,
  clearUaePassCallbackProcessing,
  clearUaePassAuthContext,
  saveUaePassPendingResult,
  readUaePassPendingResult,
  readUaePassAuthContext,
  readUaePassPostAuthTarget,
  saveUaePassPostAuthTarget,
  markUaePassNativeAuthSheetCleanupPending,
} from '../../lib/uae-pass-session';
import {
  UAE_PASS_ERROR_COPY,
  isUaePassLoginCancellation,
  resolveUaePassErrorType,
} from '../../lib/uae-pass-copy';
import { getPostAuthDestinationHref } from '../../lib/auth-handoff';
import {
  openUaePassLogoutUrl,
  refreshAuthenticatedUser,
} from '../../lib/app-auth';
import { UAE_PASS_STATUS_QUERY_KEY } from '../../hooks/useUaePassStatus';
import { AUTH_LINKED_ACCOUNTS_QUERY_KEY } from '../../hooks/useAuth';
import { clearUaePassNativeResumeWaitingForCallback } from '../../lib/uae-pass-native-resume';

function maskValue(value?: string | null, visible = 8) {
  if (!value) {
    return undefined;
  }

  if (value.length <= visible) {
    return value;
  }

  return `${value.slice(0, visible)}…`;
}

function parseCallbackPayload(params: {
  code?: string | string[];
  error?: string | string[];
  state?: string | string[];
  url?: string | string[];
}) {
  const code = Array.isArray(params.code) ? params.code[0] : params.code;
  const error = Array.isArray(params.error) ? params.error[0] : params.error;
  const state = Array.isArray(params.state) ? params.state[0] : params.state;
  const url = Array.isArray(params.url) ? params.url[0] : params.url;

  if (!url) {
    return { code, error, state };
  }

  try {
    const parsedUrl = new URL(url);
    return {
      code: parsedUrl.searchParams.get('code') ?? code,
      error: parsedUrl.searchParams.get('error') ?? error,
      state: parsedUrl.searchParams.get('state') ?? state,
    };
  } catch {
    return { code, error, state };
  }
}

export default function UaePassCallbackScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const params = useLocalSearchParams<{
    code?: string;
    error?: string;
    state?: string;
    url?: string;
  }>();

  useEffect(() => {
    let isMounted = true;

    const processCallback = async () => {
      const callbackPayload = parseCallbackPayload(params);
      const authContext = readUaePassAuthContext(callbackPayload.state);
      const callbackFingerprint =
        callbackPayload.code && callbackPayload.state
          ? `${callbackPayload.state}:${callbackPayload.code}`
          : null;

      console.info('[uae-pass][callback] start', {
        hasAuthContext: Boolean(authContext),
        mode: authContext?.mode,
        clientType: authContext?.clientType,
        redirectUri: authContext?.redirectUri,
        code: maskValue(callbackPayload.code),
        state: maskValue(callbackPayload.state),
        error: callbackPayload.error,
        fingerprint: maskValue(callbackFingerprint),
      });
      clearUaePassNativeResumeWaitingForCallback();

      if (
        callbackFingerprint &&
        !beginUaePassCallbackProcessing(callbackFingerprint)
      ) {
        const hasPendingManualLink = Boolean(readUaePassPendingResult());
        console.warn('[uae-pass][callback] duplicate fingerprint skipped', {
          fingerprint: maskValue(callbackFingerprint),
          hasPendingManualLink,
        });

        if (hasPendingManualLink) {
          router.replace('/uae-pass/link');
          return;
        }

        if (authContext?.mode === 'settings_link') {
          router.dismissTo('/settings' as never);
          return;
        }

        router.dismissTo('/');
        return;
      }

      if (!authContext) {
        clearUaePassCallbackProcessing(callbackFingerprint ?? undefined);
        console.warn('[uae-pass][callback] missing auth context');
        router.replace({
          pathname: '/uae-pass/error',
          params: {
            type: 'cancelled',
            returnTo: 'login',
          },
        });
        return;
      }

      if (authContext.locale && i18n.language !== authContext.locale) {
        await i18n.changeLanguage(authContext.locale);
      }

      if (isUaePassLoginCancellation(callbackPayload.error)) {
        clearUaePassCallbackProcessing(callbackFingerprint ?? undefined);
        clearUaePassAuthContext(authContext.state);
        console.warn('[uae-pass][callback] user cancelled', {
          error: callbackPayload.error,
        });
        router.replace({
          pathname: '/uae-pass/error',
          params: {
            type: 'cancelled',
            returnTo: authContext.mode === 'settings_link' ? 'settings' : 'login',
          },
        });
        return;
      }

      if (!callbackPayload.code || !callbackPayload.state) {
        clearUaePassCallbackProcessing(callbackFingerprint ?? undefined);
        clearUaePassAuthContext(authContext.state);
        console.warn('[uae-pass][callback] missing code or state', {
          code: maskValue(callbackPayload.code),
          state: maskValue(callbackPayload.state),
        });
        router.replace({
          pathname: '/uae-pass/error',
          params: {
            type: 'auth_failed',
            returnTo: authContext.mode === 'settings_link' ? 'settings' : 'login',
          },
        });
        return;
      }

      if (callbackPayload.state !== authContext.state) {
        clearUaePassCallbackProcessing(callbackFingerprint ?? undefined);
        console.warn('[uae-pass][callback] state mismatch', {
          expected: maskValue(authContext.state),
          received: maskValue(callbackPayload.state),
        });
        router.replace({
          pathname: '/uae-pass/error',
          params: {
            type: 'state_failed',
            returnTo: authContext.mode === 'settings_link' ? 'settings' : 'login',
          },
        });
        return;
      }

      clearUaePassAuthContext(authContext.state);

      try {
        if (authContext.mode === 'settings_link') {
          console.info('[uae-pass][callback] linking account');
          await linkUaePass({
            clientType: authContext.clientType,
            code: callbackPayload.code,
            state: callbackPayload.state,
            redirectUri: authContext.redirectUri,
          });

          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: UAE_PASS_STATUS_QUERY_KEY,
            }),
            queryClient.invalidateQueries({
              queryKey: AUTH_LINKED_ACCOUNTS_QUERY_KEY,
            }),
            // `isUaePassVerified` lives on the cached auth user, which the two
            // invalidations above do not touch. Without this the CV badge keeps
            // rendering the pre-link state (Settings says linked, the badge does
            // not appear) until the next sign-in. Never fatal to the link.
            refreshAuthenticatedUser().catch(() => null),
          ]);

          if (isMounted) {
            console.info('[uae-pass][callback] link success');
            clearUaePassCallbackProcessing(callbackFingerprint ?? undefined);
            router.dismissTo('/settings' as never);
          }
          return;
        }

        console.info('[uae-pass][callback] authenticating with backend');
        const result = await authenticateWithUaePass({
          clientType: authContext.clientType,
          code: callbackPayload.code,
          state: callbackPayload.state,
          redirectUri: authContext.redirectUri,
        });

        console.info('[uae-pass][callback] backend result', {
          status: result.status,
          isNewUser: 'isNewUser' in result ? result.isNewUser : undefined,
          matchType: 'matchType' in result ? result.matchType : undefined,
          matchedExistingAccount:
            'matchedExistingAccount' in result
              ? result.matchedExistingAccount
              : undefined,
        });

        if (result.status === 'manual_linking_required') {
          saveUaePassPendingResult(result);
          if (isMounted) {
            console.info('[uae-pass][callback] redirecting to manual link');
            router.replace('/uae-pass/link');
          }
          return;
        }

        const pendingPostAuthTarget = readUaePassPostAuthTarget();

        if (result.isNewUser) {
          if (isMounted) {
            console.info('[uae-pass][callback] redirecting to onboarding handoff');
            clearUaePassCallbackProcessing(callbackFingerprint ?? undefined);
            const destinationTarget = pendingPostAuthTarget ?? 'onboarding-intent';
            if (Platform.OS === 'web') {
              router.dismissTo(getPostAuthDestinationHref(destinationTarget));
            } else {
              markUaePassNativeAuthSheetCleanupPending();
              saveUaePassPostAuthTarget(destinationTarget);
              router.dismissTo('/');
            }
          }
          return;
        }

        if (isMounted) {
          console.info('[uae-pass][callback] redirecting home');
          clearUaePassCallbackProcessing(callbackFingerprint ?? undefined);
          markUaePassNativeAuthSheetCleanupPending();
          router.dismissTo(getPostAuthDestinationHref(pendingPostAuthTarget ?? 'root'));
        }
      } catch (error) {
        clearUaePassCallbackProcessing(callbackFingerprint ?? undefined);
        const message =
          error instanceof Error && error.message
            ? error.message
            : UAE_PASS_ERROR_COPY.authFailed;
        const errorCode =
          typeof (error as { code?: unknown })?.code === 'string'
            ? (error as { code: string }).code
            : undefined;
        const errorType = resolveUaePassErrorType(errorCode ?? message);
        const uaePassLogoutUrl =
          typeof (error as { uaePassLogoutUrl?: unknown })?.uaePassLogoutUrl ===
          'string'
            ? (error as { uaePassLogoutUrl: string }).uaePassLogoutUrl
            : undefined;

        if (!isMounted) {
          return;
        }

        console.error('[uae-pass][callback] backend error', {
          message,
          fingerprint: maskValue(callbackFingerprint),
        });

        if (
          uaePassLogoutUrl &&
          (errorType === 'sop1' ||
            errorType === 'sop2' ||
            errorType === 'ineligible')
        ) {
          console.info('[uae-pass][callback] terminating restricted UAE PASS session', {
            type: errorType,
          });
          await openUaePassLogoutUrl(uaePassLogoutUrl);
          return;
        }

        router.replace({
          pathname: '/uae-pass/error',
          params: {
            type: errorType,
            message,
            returnTo: authContext.mode === 'settings_link' ? 'settings' : 'login',
          },
        });
      }
    };

    void processCallback();

    return () => {
      isMounted = false;
    };
  }, [i18n, params, queryClient, router]);

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      <ActivityIndicator size="small" color={theme.text} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
});
